import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Chip, Modal, Skeleton, StateBlock, useToast } from '@w7/ui'
import { useState } from 'react'
import { cmApi, type ConsoleMfaMethod, type ConsoleMfaRow } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isStaffCaller } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { ROLE_LABEL, levelToRole } from '@/features/hierarchy/hierarchy'
import {
  confirmCopy,
  isConsoleMfaAgentLevel,
  methodLabel,
  methodTone,
  type ConsoleMfaConfirmIntent,
} from './consoleMfaCopy'
import './console-mfa.css'

async function loadRow(agentId: string, username?: string): Promise<ConsoleMfaRow | null> {
  const q = username?.trim() || undefined
  const first = await cmApi.consoleMfaList(authHeaders(), {
    q,
    page: 1,
    pageSize: 50,
  })
  const hit = first.rows.find((r) => r.agentId === agentId)
  if (hit) return hit
  if (!q) return null
  const retry = await cmApi.consoleMfaList(authHeaders(), { page: 1, pageSize: 50 })
  return retry.rows.find((r) => r.agentId === agentId) ?? null
}

/**
 * Owner-CM-only Console (Keycloak) 2FA status + controls for one downline agent.
 * Used on Edit member and Give/Take (balance up/down) — not for staff or players.
 */
export function ConsoleMfaControl({
  agentId,
  username,
  level,
  compact = false,
}: {
  agentId: string
  username?: string
  level: number
  /** Tighter layout inside TransferDialog. */
  compact?: boolean
}) {
  const me = useCmMe()
  const toast = useToast()
  const qc = useQueryClient()
  const [intent, setIntent] = useState<ConsoleMfaConfirmIntent | null>(null)

  const ownerOnly = Boolean(me.data) && !isStaffCaller(me.data?.caller)
  const inScope = isConsoleMfaAgentLevel(level)

  const rowQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'row', agentId],
    enabled: ownerOnly && inScope && Boolean(agentId),
    queryFn: () => loadRow(agentId, username),
  })

  const setM = useMutation({
    mutationFn: (body: {
      action: ConsoleMfaConfirmIntent['kind']
      method?: ConsoleMfaMethod
    }) =>
      cmApi.consoleMfaSet(authHeaders(), {
        agentId,
        action: body.action,
        method: body.method,
        confirm: true,
      }),
    onSuccess: async (res) => {
      toast.success(res.message)
      setIntent(null)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'row', agentId] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'summary'] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'list'] }),
        qc.invalidateQueries({ queryKey: ['cm', 'console-mfa', 'downline-status'] }),
      ])
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  if (!ownerOnly || !inScope) return null

  const row = rowQ.data ?? null
  const copy = intent ? confirmCopy(intent) : null
  const busy = setM.isPending

  return (
    <div
      className={compact ? 'cm-mfa-control cm-mfa-control--compact' : 'cm-mfa-control'}
      data-testid="console-mfa-control"
    >
      <div className="cm-mfa-control-head">
        <strong>Console 2FA</strong>
        <span className="cm-hint">Keycloak sign-in factors · owner CM only</span>
      </div>

      {rowQ.isLoading ? (
        <Skeleton height={compact ? 56 : 72} width="100%" radius={6} />
      ) : rowQ.isError ? (
        <StateBlock icon="error" title="Could not load 2FA status" tone="error">
          {(rowQ.error as Error)?.message || 'Request failed.'}
          <div style={{ marginTop: 8 }}>
            <Button type="button" variant="secondary" size="sm" onClick={() => void rowQ.refetch()}>
              Retry
            </Button>
          </div>
        </StateBlock>
      ) : !row ? (
        <p className="cm-hint" role="status">
          No console login found for this agent yet.
        </p>
      ) : (
        <>
          <div className="cm-mfa-control-status">
            {!row.found ? (
              <Chip size="sm" tone="muted" title="No Keycloak login found">
                Unknown
              </Chip>
            ) : (
              <Chip size="sm" tone={row.mfaEnabled ? 'profit' : 'muted'}>
                {row.mfaEnabled ? 'ON' : 'OFF'}
              </Chip>
            )}
            {row.methods.map((m) => (
              <button
                key={m}
                type="button"
                className="cm-mfa-method-chip"
                title={`Remove ${methodLabel(m)}`}
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'disable-method', row, method: m })}
              >
                <Chip size="sm" tone={methodTone(m)}>
                  {methodLabel(m)} ✕
                </Chip>
              </button>
            ))}
            {row.setupRequired ? (
              <Chip size="sm" tone="warn">
                Setup next login
              </Chip>
            ) : null}
          </div>

          {row.found ? (
            <p className="cm-hint" role="status">
              {row.mfaEnabled
                ? 'On — factors above are active for console sign-in. Turn off to allow password-only until they enroll again.'
                : row.setupRequired
                  ? 'Off — they must enroll an authenticator app on next console login.'
                  : 'Off — use Require setup so they must enroll authenticator on next console login (CM cannot enroll for them).'}
            </p>
          ) : null}

          <div className="cm-mfa-actions">
            {row.mfaEnabled ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'disable-all', row })}
              >
                Turn OFF
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!row.found || busy}
                onClick={() => setIntent({ kind: 'require-setup', row })}
              >
                Turn ON (require setup)
              </Button>
            )}
            {row.setupRequired ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setIntent({ kind: 'clear-setup', row })}
              >
                Clear force
              </Button>
            ) : null}
          </div>
        </>
      )}

      {intent && copy ? (
        <Modal
          open
          onClose={() => {
            if (!busy) setIntent(null)
          }}
          title={copy.title}
          subtitle="Company Master security control — this changes Keycloak sign-in factors"
          closeDisabled={busy}
          footer={
            <div className="cm-mfa-modal-footer">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setIntent(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={copy.danger ? 'danger' : 'primary'}
                busy={busy}
                onClick={() =>
                  setM.mutate({
                    action: intent.kind,
                    method: intent.kind === 'disable-method' ? intent.method : undefined,
                  })
                }
              >
                {copy.confirmLabel}
              </Button>
            </div>
          }
        >
          <p className="cm-mfa-modal-body">{copy.body}</p>
          <div className="cm-mfa-modal-meta">
            <div>
              <span className="cm-hint">Agent</span>
              <strong>{intent.row.username || intent.row.displayName}</strong>
            </div>
            <div>
              <span className="cm-hint">Tier</span>
              <strong>{ROLE_LABEL[levelToRole(intent.row.level)]}</strong>
            </div>
            <div>
              <span className="cm-hint">Factors now</span>
              <strong>
                {intent.row.methods.length
                  ? intent.row.methods.map(methodLabel).join(', ')
                  : 'None'}
              </strong>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
